import React, {
  useState,
  useEffect,
} from 'react';
import {
  IonFooter,
  IonIcon,
  IonText,
  IonRouterLink,
} from '@ionic/react';
import { useLocation, useHistory } from 'react-router-dom';
import { Button, Menu, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import './Footer.css';
import { useAuthContext } from '../providers/auth-provider';
import { useStore } from '../providers/store-provider';
import { useAppState } from '../providers/app-state/app-state-provider';
import { feedSelector } from '../redux/slices/feed';
import appStrings from '../common/app_strings';
import { buttonNoSelectStyles } from '../theme/styles';
import { useUserPermissionsQuery } from '../redux/apis/permissions.api';
import USER_PERMISSIONS from '../permissions/user.permissions';

const { CRUD_POSTS, CRUD_GOALS, VIEW_ALL_TEAMS } = USER_PERMISSIONS;

const { common: strings } = appStrings;

const Footer = (_, ref) => {
  const location = useLocation();
  const history = useHistory();
  const { activeFilter } = useSelector(feedSelector);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hasPostTargets, setHasPostTargets] = useState(false);
  const { user } = useAuthContext();
  const appState = useAppState();
  const open = Boolean(anchorEl);
  const { notificationCount } = useStore();
  const { data: userPermissions } = useUserPermissionsQuery({ user }, { skip: !user });

  const handlePostNavigation = () => {
    if (hasPostTargets) {
      history.push('/post', activeFilter);
    }
  };

  const handlePlusClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    void (async () => {
    if (user?.isFlagged && user?.role !== 'admin') {
      return;
    }

    if (appState.user?.postTargets) {
      const { groups, teams } = appState.user.postTargets;
      const hasTargets = !!(groups?.length || teams?.length);
      setHasPostTargets(hasTargets);
    }
  })();
  }, [user, appState]);

  const canCreatePost = userPermissions?.[CRUD_POSTS]?.allowed && hasPostTargets;
  const isPostGoalMenuEnabled = canCreatePost || userPermissions?.[CRUD_GOALS]?.allowed;
  const goalsDisabled = !userPermissions?.[CRUD_GOALS]?.allowed;

  return (
    <IonFooter class="ion-no-border" ref={ref}>
      <div className="footer-container">
        {user && (
          <IonRouterLink routerLink="/tabs/feed">
            <div className="footer-button">
              <img
                alt="explore-icon"
                src={
                  location.pathname === '/tabs/feed'
                    ? '/assets/icon/footer-icons/Explore-Dark.svg'
                    : '/assets/icon/footer-icons/Explore.svg'
                }
              />
              <br />
              {strings.explore}
            </div>
          </IonRouterLink>
        )}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        {user && (
          <IonRouterLink
            routerLink="/tabs/goals"
            disabled={goalsDisabled}
            onClick={(event) => {
              if (goalsDisabled) {
                event.stopPropagation();
                event.preventDefault();
              }
            }}
          >
            <div className={`footer-button ${goalsDisabled ? 'disabled' : ''}`}>
              <img
                alt="goal icon"
                style={{
                  cursor: goalsDisabled ? 'not-allowed' : 'pointer',
                  opacity: goalsDisabled ? 0.5 : 1,
                }}
                src={
                  location.pathname.startsWith('/tabs/goals')
                    ? '/assets/icon/footer-icons/Goals-Dark.svg'
                    : '/assets/icon/footer-icons/Goals.svg'
                }
              />
              <br />
              {strings.goals}
            </div>
          </IonRouterLink>
        )}
        {isPostGoalMenuEnabled && (
          <Menu
            id="plus-tab-popup"
            open={open}
            onClose={handleClose}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
          >
            {canCreatePost && (
              <MenuItem disableRipple onClick={handlePostNavigation}>
                {strings.addPost}
              </MenuItem>
            )}
            {userPermissions?.[CRUD_GOALS]?.allowed && (
              <MenuItem disableRipple>
                <IonRouterLink routerLink="/goals-create">
                  {strings.addGoal}
                </IonRouterLink>
              </MenuItem>
            )}
          </Menu>
        )}
        <div>
          <Button
            className="footer-button"
            onClick={handlePlusClick}
            disabled={!isPostGoalMenuEnabled}
            style={buttonNoSelectStyles}
          >
            <IonIcon
              src="/assets/icon/orange-circle-filled.svg"
              className="plus-button"
              color={!isPostGoalMenuEnabled ? 'unverified' : 'warning'}
            />
          </Button>
        </div>
        <IonRouterLink routerLink="/tabs/updates">
          <div className="footer-button">
            <div className="notification-icon-container">
              <img
                alt="updates icon"
                src={
                  location.pathname === '/tabs/updates'
                    ? '/assets/icon/footer-icons/Update-filled.svg'
                    : '/assets/icon/footer-icons/Update.svg'
                }
              />
              {notificationCount > 0 && (
                <div className="notification-dot">{notificationCount}</div>
              )}
            </div>
            <IonText>
              {strings.updates}
            </IonText>
          </div>
        </IonRouterLink>
        {userPermissions?.[VIEW_ALL_TEAMS]?.allowed ? (
          <IonRouterLink routerLink="/tabs/teams/admin">
            <div className="footer-button">
              <img
                alt="team icon"
                src={
                  location.pathname === '/tabs/teams/admin'
                    ? '/assets/icon/footer-icons/Teams-Dark.svg'
                    : '/assets/icon/footer-icons/Teams.svg'
                }
              />
              <br />
              {strings.panels}
            </div>
          </IonRouterLink>
        ) : (
          <IonRouterLink routerLink="/tabs/backr/teams">
            <div className="footer-button">
              <img
                alt="team icon"
                src={
                  location.pathname.startsWith('/tabs/backr/teams')
                    || location.pathname.startsWith('/team-view/')
                    || location.pathname.startsWith('/tabs/teams')
                    ? '/assets/icon/footer-icons/Teams-Dark.svg'
                    : '/assets/icon/footer-icons/Teams.svg'
                }
              />
              <br />
              <IonText>
                {strings.panels}
              </IonText>
            </div>
          </IonRouterLink>
        )}
      </div>
    </IonFooter>
  );
};

export default React.forwardRef(Footer);
